import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CreativeImportRowDto, MarketplaceFulfillmentStatus } from "@nexus/contracts";
import {
  Input,
  SearchableCombobox,
  cn,
} from "@nexus/ui";
import { AlertTriangle } from "lucide-react";
import type { AdminVendorRow } from "@/features/admin/marketplace/types/admin-marketplace.types";
import {
  filterImportRows,
  rowNeedsAttention,
  type RowFilter,
  type VendorLabelGroup,
} from "./import-readiness";
import { resolveImportRowStatus, rowIsInProduction } from "./import-row-status";

const STATUS_OPTIONS: MarketplaceFulfillmentStatus[] = [
  "SCRIPT",
  "SENT_TO_PRODUCTION",
  "SUBMITTED_FOR_FEEDBACK",
  "SENT_FOR_REVISION",
  "SUBMITTED_FOR_FINAL_APPROVAL",
  "ORDER_DELIVERED",
  "COMPLETED_PRE_ADGE",
];

interface ImportRowScriptNameInputProps {
  row: CreativeImportRowDto;
  placeholder: string;
  onCommit: (hook: string | null) => void;
}

/** Keeps script name edits local until blur so in-flight PATCH responses cannot clobber typing. */
export function ImportRowScriptNameInput({
  row,
  placeholder,
  onCommit,
}: ImportRowScriptNameInputProps) {
  const [draft, setDraft] = useState(row.hook ?? "");
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    setDraft(row.hook ?? "");
  }, [row.id]);

  useEffect(() => {
    if (!isFocused) {
      setDraft(row.hook ?? "");
    }
  }, [row.hook, isFocused]);

  function commitDraft() {
    const next = draft.trim() === "" ? null : draft;
    const current = row.hook?.trim() === "" ? null : row.hook ?? null;
    if (next !== current) {
      onCommit(next);
    }
  }

  return (
    <Input
      className="w-full min-w-0 text-sm"
      value={draft}
      placeholder={placeholder}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false);
        commitDraft();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.currentTarget.blur();
        }
      }}
      onChange={(event) => setDraft(event.target.value)}
    />
  );
}

interface ImportRowsTableProps {
  rows: CreativeImportRowDto[];
  vendorLabelGroups: VendorLabelGroup[];
  vendors: AdminVendorRow[];
  batchColumnDetected?: boolean;
  className?: string;
  onRowPatch: (
    row: CreativeImportRowDto,
    patch: {
      vendorId?: string | null;
      hook?: string | null;
      clientStartDate?: string | null;
      expectedDeliveryDate?: string | null;
      fulfillmentStatus?: MarketplaceFulfillmentStatus | null;
    },
  ) => void;
}

export function ImportRowsTable({
  rows,
  vendorLabelGroups,
  vendors,
  batchColumnDetected = true,
  className,
  onRowPatch,
}: ImportRowsTableProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<RowFilter>("all");

  const counts = useMemo(
    () => ({
      all: rows.length,
      needsAttention: rows.filter((row) => rowNeedsAttention(row, vendorLabelGroups)).length,
      ready: rows.filter(
        (row) => row.willCommit && !rowNeedsAttention(row, vendorLabelGroups),
      ).length,
      archive: rows.filter((row) => !row.willCommit).length,
    }),
    [rows, vendorLabelGroups],
  );

  const filteredRows = useMemo(
    () => filterImportRows(rows, filter, vendorLabelGroups),
    [rows, filter, vendorLabelGroups],
  );

  const tabs: { id: RowFilter; label: string; count: number }[] = [
    { id: "all", label: t("marketplace.admin.import.table.tabs.all"), count: counts.all },
    {
      id: "needsAttention",
      label: t("marketplace.admin.import.table.tabs.needsAttention"),
      count: counts.needsAttention,
    },
    { id: "ready", label: t("marketplace.admin.import.table.tabs.ready"), count: counts.ready },
    { id: "archive", label: t("marketplace.admin.import.table.tabs.archive"), count: counts.archive },
  ];

  const scrollClassName =
    "min-h-0 min-w-0 flex-1 overflow-auto [scrollbar-gutter:stable] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-muted/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50";

  return (
    <section
      className={cn(
        "flex min-h-[65vh] min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border/60 bg-card",
        className,
      )}
    >
      <div className="border-b border-border/50 px-5 py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold">{t("marketplace.admin.import.table.title")}</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("marketplace.admin.import.table.subtitle")}{" "}
              <span className="hidden text-muted-foreground/80 xl:inline">
                {t("marketplace.admin.import.table.scrollHint")}
              </span>
            </p>
          </div>
          <div className="flex flex-wrap gap-1 rounded-lg border border-border/60 bg-muted/30 p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
                  filter === tab.id
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => setFilter(tab.id)}
              >
                {tab.label}
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums">
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={scrollClassName}>
        <table className="w-full min-w-[58rem] table-fixed text-sm">
          <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm">
            <tr className="border-b border-border/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground/70">
              <th className="w-12 whitespace-nowrap px-3 py-2">#</th>
              <th className="w-[14%] whitespace-nowrap px-3 py-2">{t("marketplace.admin.import.table.batch")}</th>
              <th className="w-[18%] whitespace-nowrap px-3 py-2">{t("marketplace.admin.import.table.asset")}</th>
              <th className="w-[14%] whitespace-nowrap px-3 py-2">{t("marketplace.admin.import.table.sheetPartner")}</th>
              <th className="w-[18%] whitespace-nowrap px-3 py-2">{t("marketplace.admin.import.table.assignTo")}</th>
              <th className="w-[12%] whitespace-nowrap px-3 py-2">{t("marketplace.admin.import.table.status")}</th>
              <th className="w-[24%] whitespace-nowrap px-3 py-2">{t("marketplace.admin.import.table.productionDates")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  {t("marketplace.admin.import.table.emptyFilter")}
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => {
                const inProduction = rowIsInProduction(row);
                const rowStatus = resolveImportRowStatus(row);

                return (
                  <tr key={row.id} className="align-top hover:bg-muted/30">
                    <td className="px-3 py-3 tabular-nums text-muted-foreground">
                      {row.sheetRowNumber}
                    </td>
                    <td className="px-3 py-3">
                      {row.batch ? (
                        <div>
                          <span className="font-medium">{row.batch}</span>
                          {!batchColumnDetected && row.population === "LIVE" ? (
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {t("marketplace.admin.import.batchFromFileName")}
                            </p>
                          ) : null}
                        </div>
                      ) : row.population === "LIVE" ? (
                        <span className="flex items-center gap-1 text-xs text-status-warning">
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          {t("marketplace.admin.import.batchMissing")}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <ImportRowScriptNameInput
                        row={row}
                        placeholder={
                          row.assetName ?? t("marketplace.admin.import.table.scriptNamePlaceholder")
                        }
                        onCommit={(hook) => onRowPatch(row, { hook })}
                      />
                      {row.assetName ? (
                        <p className="mt-1 truncate text-xs text-muted-foreground" title={row.assetName}>
                          {row.assetName}
                        </p>
                      ) : null}
                      {!row.hasBrief ? (
                        <p className="mt-1 flex items-center gap-1 text-xs text-status-warning">
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          {t("marketplace.admin.import.archiveNoBrief")}
                        </p>
                      ) : null}
                      {row.briefUrl ? (
                        <a
                          href={row.briefUrl}
                          className="text-xs text-primary hover:underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("marketplace.admin.import.table.briefLink")}
                        </a>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {row.vendorLabel ? (
                        <div className="space-y-1">
                          <span className="block text-sm">{row.vendorLabel}</span>
                          {row.pendingVendorContactEmail ? (
                            <span className="block text-xs">
                              {t("marketplace.admin.import.newPartnerCaptured", {
                                email: row.pendingVendorContactEmail,
                              })}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span>—</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {inProduction ? (
                        <SearchableCombobox
                          items={vendors}
                          getKey={(vendor) => vendor.id}
                          getLabel={(vendor) => vendor.name}
                          value={row.vendorId}
                          placeholder={t("marketplace.admin.import.assignToPlaceholder")}
                          searchPlaceholder={t("marketplace.admin.import.partnerSearch")}
                          emptyMessage={t("marketplace.admin.import.partnerEmpty")}
                          triggerClassName="w-full min-w-0"
                          onSelect={(vendor) => onRowPatch(row, { vendorId: vendor.id })}
                        />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <select
                        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                        value={rowStatus}
                        onChange={(event) =>
                          onRowPatch(row, {
                            fulfillmentStatus: event.target.value as MarketplaceFulfillmentStatus,
                          })
                        }
                      >
                        {STATUS_OPTIONS.map((value) => (
                          <option key={value} value={value}>
                            {t(`marketplace.admin.import.status.${value}`)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-3">
                      {inProduction ? (
                        <div className="min-w-[12rem] space-y-2.5">
                          <div className="space-y-1">
                            <label
                              className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                              htmlFor={`import-row-${row.id}-start`}
                            >
                              {t("marketplace.admin.import.tableClientStart")}
                            </label>
                            <Input
                              id={`import-row-${row.id}-start`}
                              type="date"
                              className="w-full min-w-[9.5rem]"
                              value={row.clientStartDate ?? ""}
                              onChange={(event) =>
                                onRowPatch(row, {
                                  clientStartDate: event.target.value || null,
                                })
                              }
                            />
                            {row.clientStartDatePrefilled ? (
                              <p className="flex items-center gap-1 text-xs text-status-warning">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                {t("marketplace.admin.import.clientStartDatePrefilled")}
                              </p>
                            ) : null}
                          </div>
                          <div className="space-y-1">
                            <label
                              className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                              htmlFor={`import-row-${row.id}-delivery`}
                            >
                              {t("marketplace.admin.import.tableExpectedDelivery")}
                            </label>
                            <Input
                              id={`import-row-${row.id}-delivery`}
                              type="date"
                              className="w-full min-w-[9.5rem]"
                              value={row.expectedDeliveryDate ?? ""}
                              onChange={(event) =>
                                onRowPatch(row, {
                                  expectedDeliveryDate: event.target.value || null,
                                })
                              }
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
